import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MagnifyingGlassIcon,
  StarIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { products, Product } from '../data/products';
import PurchaseModal from './PurchaseModal';
import PackageSizeModal from './PackageSizeModal';

interface ProductListingProps {
  onProductSelect: (product: Product) => void;
}

const ProductListing: React.FC<ProductListingProps> = ({ onProductSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isPackageSizeModalOpen, setIsPackageSizeModalOpen] = useState(false);

  const categories = [
    { id: 'all', label: 'All Products' },
    { id: 'packaging-coverage', label: 'Flexcrete Waterproofing' },
    { id: 'flashing-kit', label: 'Kit' }
  ];

  // Only show actual products, not instruction/info products
  const actualProducts = products.filter(product => 
    product.id === 'packaging-coverage' || product.id === 'flashing-kit'
  );

  const filteredProducts = actualProducts.filter(product => {
    const matchesSearch = product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Product Information</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive guides, specifications, and resources for all your home improvement needs. 
              Learn professional techniques and discover the right products for your projects.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div className="flex gap-2">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              onClick={() => onProductSelect(product)}
            >
              {/* Product Image */}
              <div className="aspect-video bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-3 bg-white rounded-lg shadow-md flex items-center justify-center">
                    <span className="text-2xl">
                      {product.id === 'packaging-coverage' && '📦'}
                      {product.id === 'mixing-preparing' && '🥄'}
                      {product.id === 'applying' && '🎨'}
                      {product.id === 'flashing' && '🔧'}
                      {product.id === 'technical-specifications' && '📊'}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">{product.title}</h3>
                </div>
              </div>

              {/* Product Info */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <StarIcon key={i} className="w-4 h-4 fill-current" />
                      ))}
                    </div>
                    <span className="ml-2 text-sm text-gray-600">(4.8/5)</span>
                  </div>
                  <span className="text-sm font-medium text-green-600">{product.availability}</span>
                </div>

                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {product.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-primary-600">{product.price}</div>
                  <div className="flex gap-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Save product to localStorage cache
                        try {
                          const savedProducts = JSON.parse(localStorage.getItem('savedProducts') || '[]');
                          const isAlreadySaved = savedProducts.some((p: { id: string }) => p.id === product.id);
                          
                          if (isAlreadySaved) {
                            // Remove from saved if already saved
                            const updatedProducts = savedProducts.filter((p: { id: string }) => p.id !== product.id);
                            localStorage.setItem('savedProducts', JSON.stringify(updatedProducts));
                          } else {
                            // Add to saved
                            savedProducts.push({
                              id: product.id,
                              title: product.title,
                              subtitle: product.subtitle
                            });
                            localStorage.setItem('savedProducts', JSON.stringify(savedProducts));
                          }
                        } catch (error) {
                          console.error('Error saving product:', error);
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Save to favorites"
                    >
                      <HeartIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button 
                    onClick={() => {
                      if (product.id === 'packaging-coverage') {
                        // Show package size selection modal for packaging product
                        setIsPackageSizeModalOpen(true);
                      } else if (product.stripeCheckoutUrl) {
                        // Open Stripe checkout in new tab for products with Stripe URL
                        window.open(product.stripeCheckoutUrl, '_blank');
                      } else {
                        // Open purchase modal for other products
                        setIsPurchaseModalOpen(true);
                      }
                    }}
                    className="flex-1 btn-primary text-sm"
                  >
                    Buy Now
                  </button>
                  <button 
                    onClick={() => onProductSelect(product)}
                    className="flex-1 btn-secondary text-sm"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* No Results */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <MagnifyingGlassIcon className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        )}

        {/* Features Section */}
        <div className="mt-16 bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Why Choose Our Product Guides?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📚</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Comprehensive Guides</h3>
              <p className="text-gray-600 text-sm">
                Detailed step-by-step instructions with professional tips and troubleshooting advice.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎥</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Video Tutorials</h3>
              <p className="text-gray-600 text-sm">
                Watch professional demonstrations and learn techniques from industry experts.
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🛠️</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Tool Recommendations</h3>
              <p className="text-gray-600 text-sm">
                Get expert recommendations on tools and equipment for optimal results.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      <PurchaseModal 
        isOpen={isPurchaseModalOpen} 
        onClose={() => setIsPurchaseModalOpen(false)} 
      />

      {/* Package Size Selection Modal */}
      <PackageSizeModal 
        isOpen={isPackageSizeModalOpen} 
        onClose={() => setIsPackageSizeModalOpen(false)} 
      />
    </div>
  );
};

export default ProductListing;
